import './mailCodePinVerifyPage.scss';

import { ThemeProvider } from '@mui/material/styles';
import { toPhoneString } from '@thesavingsgroup/utils/toPhoneString';
import isEmpty from 'lodash/isEmpty';
import uniqueId from 'lodash/uniqueId';
import { FormEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import vehicleSUVTopView from '../../../assets/vehicle-suv-topview.png';
import { Alert } from '../../../components/Alert/Alert';
import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { CheckCircleIcon } from '../../../components/Icons/CheckCircleIcon';
import { ErrorOutlineIcon } from '../../../components/Icons/ErrorOutlineIcon';
import { OTP } from '../../../components/OTP/OTP';
import { Toast } from '../../../components/Toast/Toast';
import useApiStore from '../../../store/apiStore/useApiStore';
import useApplicantStore from '../../../store/applicantStore/useApplicantStore';
import { HydrateSource } from '../../../store/applyStore/enums/HydrateSource';
import useApplyStore from '../../../store/applyStore/useApplyStore';
import useMailCodeStore from '../../../store/mailCodeStore/useMailCodeStore';
import useParameterStore from '../../../store/parameterStore/useParameterStore';
import { DeliverPinLocation } from '../../../store/pinStore/enums/DeliverPinLocation';
import { useCountdownTimer } from '../../../store/pinStore/hooks/useCountdownTimer';
import usePinStore from '../../../store/pinStore/usePinStore';
import { useNavigateWithCallback } from '../../../store/routeStore/hooks/useNavigateWithCallback';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useUserStore from '../../../store/userStore/useUserStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();
  const navigateWithCallback = useNavigateWithCallback();

  const { t } = useTranslation();
  const { isLoading } = useApiStore();
  const { applicationType, leadChannelCode } = useParameterStore();
  const { hydrate } = useApplyStore();
  const { updateCurrentResidencePhysicalAddress } = useApplicantStore();
  const {
    clearPin,
    deliverPin,
    deliverPinFailedMessage,
    deliverPinRetrySuccess,
    pin,
    setPin,
    computed: { hasValidPin },
  } = usePinStore();
  const {
    computed: {
      applicantPhysicalAddress,
      hasValidApplicantPhysicalAddress,
      isPrimaryPhoneNumberEmpty,
      preApprovalAmountAsNumber,
      vehiclePayoffAmount,
      vehicleVin,
    },
    firstName,
    lastName,
    primaryPhoneNumber,
    primaryEmailAddress,
    resolveMailCodeState,
    saveMailCodeDataByUniqueIdentifier,
  } = useMailCodeStore();
  const {
    clearPinPurpose,
    clearPinRetryState,
    setUserFirstName,
    setUserLastName,
    setUserSignedUp,
    userAuthenticate,
    userCreate,
    userUpdate,
    userCreateFailedMessage,
    userAuthenticateFailedMessage,
    computed: { shouldCreateUser, isPinPurposeEmpty },
    pinPurpose,
    getDeliverPinParams,
  } = useUserStore();

  const resendTimerDuration = 15; // in seconds
  const [seconds, setSecondsLeft] = useState(0);
  const { startTimer, stopTimer } = useCountdownTimer(
    setSecondsLeft,
    resendTimerDuration,
  );

  const buttonText = useMemo((): string => {
    if (seconds > 0) {
      return t('MailCodePinVerifyPage.resendCodeDelay', {
        delay: seconds,
      });
    }
    return t('MailCodePinVerifyPage.resendCode');
  }, [t, seconds]);

  const formSubmit = async (event?: FormEvent): Promise<void> => {
    event?.preventDefault();
    if (!pin) {
      return;
    }
    // Set before creating/authenticating so SessionGuard does not briefly redirect
    // the still-authenticating user away from this non-authenticated route.
    setUserSignedUp(true);
    if (shouldCreateUser) {
      await userCreate(
        firstName,
        lastName,
        primaryEmailAddress,
        primaryPhoneNumber,
        pin,
      );
    } else {
      await userAuthenticate(
        primaryPhoneNumber,
        pin,
        leadChannelCode,
        pinPurpose,
      );

      // only update the profile once signed in: without a session the PATCH
      // is a 401, which redirects away from the error just shown
      if (useUserStore.getState().userAuthenticateSuccess) {
        setUserFirstName(firstName);
        setUserLastName(lastName);
        await userUpdate();
      }
    }
    const { userAuthenticateSuccess, userCreateSuccess } =
      useUserStore.getState();
    if (userCreateSuccess || userAuthenticateSuccess) {
      // like the profile update above, this needs a session
      if (hasValidApplicantPhysicalAddress) {
        await updateCurrentResidencePhysicalAddress(applicantPhysicalAddress);
      }
      const applyRoute = await hydrate({
        applicationType,
        payoffAmount: vehiclePayoffAmount,
        purchasePrice: preApprovalAmountAsNumber,
        source: HydrateSource.MAIL_CODE,
        vin: vehicleVin,
      });
      // saved before navigating so the application submit finds it however the
      // navigation ends (CP-7030)
      saveMailCodeDataByUniqueIdentifier();
      navigateWithCallback(applyRoute, (): void => {
        resolveMailCodeState();
        clearPinPurpose();
      });
    }
  };

  const resendCode = async (): Promise<void> => {
    if (primaryPhoneNumber) {
      startTimer();
      clearPinRetryState();
      const deliverPinParams = getDeliverPinParams(
        DeliverPinLocation.RETRY,
        primaryPhoneNumber,
        primaryEmailAddress,
      );
      await deliverPin(deliverPinParams);
      clearPin();
    }
  };

  useEffect((): (() => void) => {
    if (isPrimaryPhoneNumberEmpty || isPinPurposeEmpty) {
      navigate('/mail');
    }
    startTimer();
    return (): void => {
      stopTimer();
    };
  }, [primaryPhoneNumber]);

  useEffect((): void => {
    if (userCreateFailedMessage || userAuthenticateFailedMessage) {
      clearPin();
    }
  }, [userCreateFailedMessage, userAuthenticateFailedMessage]);

  useEffect((): (() => void) => {
    // Clearing pin on unmount so the next pin page starts fresh
    return (): void => {
      clearPin();
    };
  }, []);

  useEffect((): void => {
    // Auto-submit once the last digit is entered
    if (hasValidPin) {
      formSubmit();
    }
  }, [hasValidPin]);

  return (
    <ThemeProvider theme={theme}>
      <div className="page mail-code-pin-verify-page">
        <Toast
          open={deliverPinRetrySuccess}
          onClose={(): void => {
            usePinStore.setState({ deliverPinRetrySuccess: false });
          }}
        >
          <Alert
            data-testid={locator.pages.mailCodePinVerify.alert.success}
            icon={<CheckCircleIcon />}
            severity="success"
          >
            {t('MailCodePinVerifyPage.codeSent')}
          </Alert>
        </Toast>

        <Toast
          open={!isEmpty(deliverPinFailedMessage)}
          onClose={(): void => {
            usePinStore.setState({ deliverPinFailedMessage: null });
          }}
        >
          <Alert
            data-testid={locator.pages.mailCodePinVerify.alert.error}
            icon={<ErrorOutlineIcon />}
            severity="error"
          >
            {deliverPinFailedMessage}
          </Alert>
        </Toast>

        <Header showLogo splitLayout />

        <main>
          <figure>
            <img
              alt={t('imageAlts.vehicleSUVTopView')}
              src={vehicleSUVTopView}
            />
          </figure>
          <form noValidate onSubmit={formSubmit}>
            <div className="form-content">
              <h1 data-testid={locator.pages.mailCodePinVerify.form.h1}>
                {t('MailCodePinVerifyPage.title')}
              </h1>
              <h3 data-testid={locator.pages.mailCodePinVerify.form.h3}>
                <Trans
                  components={[<u key={uniqueId('span-')} />]}
                  i18nKey={'MailCodePinVerifyPage.subtitle'}
                  values={{
                    phoneNumber: toPhoneString(primaryPhoneNumber),
                  }}
                />
              </h3>

              <OTP
                disabled={isLoading}
                error={userCreateFailedMessage || userAuthenticateFailedMessage}
                errorTestId={locator.pages.mailCodePinVerify.form.pinInputError}
                inputTestId={locator.pages.mailCodePinVerify.form.pinInput}
                value={pin}
                onChange={setPin}
              />

              <Button
                data-testid={locator.pages.mailCodePinVerify.form.resendPinLink}
                disabled={seconds > 0}
                loader={isLoading}
                fullWidth
                onClick={resendCode}
              >
                {buttonText}
              </Button>
            </div>
            <footer data-testid={locator.pages.mailCodePinVerify.footer.root}>
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </div>
    </ThemeProvider>
  );
}

Component.displayName = 'MailCodePinVerifyPage';
